import { Box, Button, Card, CardActions, CardContent, CardHeader, Collapse, Radio, Theme, useTheme } from "@mui/material";
import React, { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext  } from "react-hook-form";
import { EventRepeatOutlined, ExpandMore} from "@mui/icons-material";
import { AlarmSchemeTierCard } from "../alarm-scheme-tier-card/AlarmSchemeTierCard";
import ActionMenu from "../action-menu/ActionMenu";
import { AlarmSchemeNotificationListType, AlarmSchemeScheduledNotificationType, AlarmSchemeTierType } from "../alarm-scheme-tier-card/AlarmSchemeTierUtils";
import { ALWAYS_SCHEDULE } from "../../services/alarm-scheme-data-service/Models";
import { AlarmSchemeItem, AlarmSchemeListActionEnum, AlarmSchemeListActionItem, AlarmSchemesFormType } from "./AlarmSchemeListUtils";
import { getObjectTypeName } from "../../common/utilities/GetObjectTypeName";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { AlarmSchemeUsageCard } from "./AlarmSchemeUsageCard";
import { IeLynxObject } from "../../common/models/eLynxObject";
import NoData from "../no-data/NoData";
import { SxProps } from "@mui/system";


export type AlarmSchemeListItemProps = {
    item: AlarmSchemeItem,
    allowEdit: boolean;
    onAction?: (action: AlarmSchemeListActionEnum, payload: IeLynxObject) => void,
    onSelected?: (ndx: number, value: boolean) => void;
    enableSelection: boolean;
    showAvatar?: boolean;
    showSetpoints?: boolean;
    sx?: SxProps<Theme>;
}

export function AlarmSchemeListItem({
    item,
    allowEdit,
    onAction,
    onSelected,
    enableSelection,
    showAvatar,
    showSetpoints,
    sx
}: AlarmSchemeListItemProps) {

    const [expanded, setExpanded] = useState(false);
    const [usageExpanded, setUsageExpanded] = useState(false);
    const { t } = useTranslation();

    const theme = useTheme();


    const actionItems: AlarmSchemeListActionItem[] = useMemo(() => {

        let actions = [{

            label: t("buttons.editAlarmScheme"),
            action: AlarmSchemeListActionEnum.Edit,
            order: 0,
            onClick: () => {
                onAction && onAction(AlarmSchemeListActionEnum.Edit, {
                    id: item.id,
                    objectType: eLynxObjectTypeEnum.AlarmScheme,
                    displayText: item.name
                });
            },
        }]; 

        if (item.setpoints.length === 0) {

            actions.push({
                label: t("buttons.deleteAlarmScheme"),
                action: AlarmSchemeListActionEnum.Edit,
                order: 1,
                onClick: () => {
                    onAction && onAction(AlarmSchemeListActionEnum.Delete, {
                        id: item.id,
                        objectType: eLynxObjectTypeEnum.AlarmScheme,
                        displayText: item.name
                    });
                },
            })
        }
        
        
        return actions;

    }, [
        item,
        t,
        onAction
    ]);

    const {
        watch,
    } = useFormContext<AlarmSchemesFormType>();    

    const [
        alarmSchemes,
    ] = watch([
        'alarmSchemes',
    ]);


    // we need the 'real' index of the item
    const index = alarmSchemes.findIndex(o => o.id === item.id);

    const tiers = item.tiers.map(o => {
        const tier: AlarmSchemeTierType = {
            id: o.id,
            name: o.name,
            description: o.description,
            schedules: [],
            scheduledNotifications: o.schedules.map(v => {
                return {

                    scheduleID: v.id,
                    scheduleName: v.name,
                
                    organizationID: -1,
                    organizationName: '',
                    notifications: v.notificationLists.map(n => {
                        return {
                            notificationListID: n.id,
                            notificationListName: n.name
                        } as AlarmSchemeNotificationListType;
        
                    })
                
                } as AlarmSchemeScheduledNotificationType;

            }),
            notifications: o.notifications.map(v => {
                return {
                    notificationListID: v.notificationListID,
                    notificationListName: v.notificationListName ?? ''
                } as AlarmSchemeNotificationListType;
            }),
            useScheduledNotifications: o.notifications.length === 0 || o.notifications.find(v => v.scheduleID === ALWAYS_SCHEDULE) ? false : true
        };

        return tier;
    });

    function getAvatar() {
        return (
            <Box sx={{
                position: "relative",
                width: (theme) => theme.spacing(4),
                display: 'flex'
            }}
            >
                <EventRepeatOutlined fontSize="large" />
            </Box>
        );
    }

    function getTitle(): ReactNode {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flex: '1 1 auto'
                }}>
                {enableSelection && 
                <Radio
                    checked={item.selected ?? false}
                    onChange={(_ev, checked) => onSelected && onSelected(index, checked)}
                    value={item.id}
                    name="alarm-schemes"
                    slotProps={{
                        input: { 'aria-label': item.name }
                    }}
                    />
                }
                <Box component='span' sx={{
                    marginTop:'auto',
                    marginBottom: 'auto'
                }}>
                    {item.name}
                </Box>
            </Box>
        );
    }

    return (
        <Card variant="outlined" sx={sx}>
            <CardHeader
                sx={{
                    padding: 1,
                }}
                title={getTitle()}
                avatar={showAvatar ? getAvatar() : undefined}
                //subheader={getSubheader()}
                //subheader={getTitle()}
                action={allowEdit ? <ActionMenu actionItems={actionItems} /> : undefined}
                slotProps={{
                    title: {
                        sx: {
                            fontSize: '1rem',
                        },
                    }
                }}
            >
            </CardHeader>
            <CardActions sx={{ paddingTop: 0, paddingBottom: 0 }}>
               <Button
                   sx={{
                       color: "text.primary"
                   }}
                   variant="text"
                   onClick={() => setExpanded(!expanded)}
                   style={{ marginLeft: "auto" }}
                   endIcon={<ExpandMore sx={{
                       transform: !expanded ? "rotate(0deg)" : "rotate(180deg)",
                       transition: theme.transitions.create('transform', {
                           duration: theme.transitions.duration.shortest,
                       })
                   }} />}
               >
                   {t('setpointAdmin.details')}
               </Button> 
           </CardActions>
            <Collapse in={expanded} timeout="auto">

                <CardContent sx={{
                    padding: (theme) => `0 ${theme.spacing(1)} ${theme.spacing(1)} ${theme.spacing(1)} !important`,
                }}>
                    {
                        tiers.map(o => 
                            <AlarmSchemeTierCard key={o.id} 
                                item={o} 
                                dense={!allowEdit} 
                                sx={{
                                    marginLeft: 4,
                                }}
                                />
                        )
                    }
                   
               </CardContent>
            </Collapse>
            {showSetpoints && 

                <>
                    <CardActions sx={{ paddingTop: 0, paddingBottom: 0 }}>
                        <Button
                            sx={{
                                color: "text.primary"
                            }}
                            variant="text"
                            onClick={() => setUsageExpanded(!usageExpanded)}
                            style={{ marginLeft: "auto" }}
                            endIcon={<ExpandMore sx={{
                                transform: !usageExpanded ? "rotate(0deg)" : "rotate(180deg)",
                                transition: theme.transitions.create('transform', {
                                    duration: theme.transitions.duration.shortest,
                                })
                            }} />}
                        >
                            {`${getObjectTypeName(eLynxObjectTypeEnum.SetPoint, true)} [${item.setpoints.length}]`}
                        </Button> 


                    </CardActions>

                    <Collapse in={usageExpanded} timeout="auto" sx={{display: 'flex'}}>

                        <CardContent sx={{
                            display: 'flex',
                            padding: (theme) => `0 ${theme.spacing(1)} ${theme.spacing(1)} ${theme.spacing(1)} !important`,
                        }}>
                            {
                                item.setpoints.length > 0 && 
                                    <AlarmSchemeUsageCard
                                        items={item.setpoints} 
                                        onAction={onAction} 
                                    />
                            }

                            {
                                item.setpoints.length === 0 && 
                                <NoData title='' text='There are no alarm setpoints using this alarm scheme' />
                            }

                        </CardContent>
                    </Collapse>
                </>
            }
        </Card>
    );
}